import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Person, MenuBook } from '@mui/icons-material';
import GradientSurface from '../../../components/shared/GradientSurface';
import { RADIUS, SHADOWS_LIGHT, SHADOWS_DARK } from '../../../theme/tokens';

/**
 * DigitalCardFront
 * Front face of the flip library card.
 * Caller is responsible for backfaceVisibility via the wrapping container.
 *
 * @param {{ name?: string, avatarUrl?: string, staticQrId?: string, qrUrl?: string, qrLoaded?: boolean }} props
 */
function DigitalCardFront({ name, avatarUrl, staticQrId, qrUrl, qrLoaded }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const glowShadow = isLight ? SHADOWS_LIGHT.glowPrimary : SHADOWS_DARK.glowPrimary;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: RADIUS.lg,
        overflow: 'hidden',
        position: 'relative',
        backfaceVisibility: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Animated top band */}
      <GradientSurface
        variant="brandDeep"
        animated
        radius={0}
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '2px solid rgba(255,255,255,0.2)',
        }}
      >
        <MenuBook sx={{ fontSize: 28, color: 'white' }} />
        <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'white', letterSpacing: 0.5 }}>
          BookMyLibrary.in
        </Typography>
      </GradientSurface>

      {/* Card label band */}
      <Box
        sx={{
          bgcolor: theme.palette.accent?.main ?? theme.palette.warning.main,
          px: 3,
          py: 1,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="overline"
          fontWeight={700}
          sx={{ color: theme.palette.secondary.main, letterSpacing: 2 }}
        >
          DIGITAL LIBRARY CARD
        </Typography>
      </Box>

      {/* Photo + info */}
      <GradientSurface
        variant="brand"
        radius={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 3,
          py: 2.5,
          gap: 1.5,
        }}
      >
        {/* Photo placeholder */}
        <Box
          sx={{
            width: 80,
            height: 100,
            borderRadius: RADIUS.md,
            border: '3px solid rgba(255,255,255,0.9)',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Person sx={{ fontSize: 64, color: 'rgba(255,255,255,0.85)' }} />
          )}
        </Box>

        {/* Name — display weight */}
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ color: 'white', textAlign: 'center', lineHeight: 1.2 }}
        >
          {name || 'Student Name'}
        </Typography>

        {/* Embossed card number in mono */}
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.85)',
            fontFamily: '"Courier New", Courier, monospace',
            letterSpacing: 2,
            fontWeight: 600,
          }}
        >
          CARD NO. {staticQrId || '0000000000'}
        </Typography>

        {/* QR well */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: RADIUS.md,
            p: 1.5,
            display: 'inline-flex',
            boxShadow: glowShadow,
          }}
        >
          {qrUrl && qrLoaded ? (
            <img src={qrUrl} alt="QR Code" style={{ width: 88, height: 88 }} />
          ) : (
            <Box
              sx={{
                width: 88,
                height: 88,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            </Box>
          )}
        </Box>
      </GradientSurface>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: theme.palette.secondary.main,
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" fontWeight={700} noWrap sx={{ color: 'white', letterSpacing: 1 }}>
          MEMBER CARD
        </Typography>
      </Box>
    </Box>
  );
}

export default DigitalCardFront;
